/**
 * FABDataEntry component - Floating Action Button for quick manual data entry.
 *
 * Features:
 * - Floating action button that opens chat sidebar for conversational logging
 * - Chat sidebar for natural language data entry (e.g., "nitrates 5")
 * - Manual form modal accessible from chat sidebar (delegated to UnifiedManualEntryModal)
 */
import { useState } from 'react';
import { Affix, ActionIcon, Transition, Tooltip, rem } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconMessagePlus } from '@tabler/icons-react';
import { useTank } from '@/contexts/TankContext';
import { ChatSidebar } from '../ChatSidebar';
import { UnifiedManualEntryModal } from '@/components/UnifiedManualEntryModal';
import classes from './FABDataEntry.module.css';

export interface FABDataEntryProps {
  /** Bottom offset for the FAB position */
  bottomOffset?: number;
  /** Right offset for the FAB position */
  rightOffset?: number;
}

export function FABDataEntry({
  bottomOffset = 20,
  rightOffset = 20,
}: FABDataEntryProps): JSX.Element | null {
  const { selectedTank } = useTank();
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [chatOpen, setChatOpen] = useState(false);

  // Handler to open manual form from chat sidebar
  const handleOpenManualForm = (): void => {
    setChatOpen(false);
    openModal();
  };

  if (!selectedTank) return null;

  return (
    <>
      {/* Floating Action Button */}
      <Affix position={{ bottom: bottomOffset, right: rightOffset }} className={classes.affix}>
        <Transition transition="slide-up" mounted={!chatOpen && !modalOpened}>
          {(styles) => (
            <Tooltip label="Log data" position="left" withArrow>
              <ActionIcon
                style={styles}
                size={56}
                radius="xl"
                variant="filled"
                color="blue"
                onClick={() => setChatOpen(true)}
                aria-label="Open data entry chat"
              >
                <IconMessagePlus
                  style={{ width: rem(28), height: rem(28) }}
                  stroke={2}
                />
              </ActionIcon>
            </Tooltip>
          )}
        </Transition>
      </Affix>

      {/* Chat Sidebar for conversational data logging */}
      <ChatSidebar
        opened={chatOpen}
        onClose={() => setChatOpen(false)}
        tankId={selectedTank.id}
        tankName={selectedTank.name}
        onOpenManualForm={handleOpenManualForm}
      />

      {/* Data Entry Modal */}
      <UnifiedManualEntryModal
        opened={modalOpened}
        onClose={closeModal}
        initialType="water-test"
      />
    </>
  );
}

export default FABDataEntry;
